/**
 * The "Menu" panel — Office's File / backstage view, as a modal dialog over
 * the layout (the host's top bar stays above it): a section column on the
 * left (Home, Open, Pinned, Recent, Open editors) and the section on the
 * right. "Open" is the WHOLE menu as a tree (`MenuTree`), so every screen at
 * every depth is reachable from here whatever the ribbon shows.
 *
 * Keyboard: focus starts on the selected section; Up / Down move between
 * sections; Tab stays inside the dialog; Esc closes it and returns focus to
 * the control that opened it.
 */
import { ArrowLeft, Clock, FolderOpen, House, Pencil, Pin } from 'lucide-react'
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { EditorsList } from '../../nav/parts/EditorsList'
import { MenuTree } from '../../nav/parts/MenuTree'
import { PinnedList, RecentList } from '../../nav/parts/ShellLists'
import { S } from '../../nav/tokens'
import type { ShellNavModel } from '../../nav/useShellNav'
import { ACCENT_SOLID, moveFocus, tabbables } from './shared'

export type PanelSection = 'home' | 'open' | 'pinned' | 'recent' | 'editors'

const h1Style = { margin: '0 0 6px', fontSize: 28, fontWeight: 300, color: S.text, lineHeight: 1.25 } as const
const h2Style = { margin: '22px 0 8px', fontSize: 16, fontWeight: 600, color: S.text } as const
const leadStyle = { margin: '0 0 16px', fontSize: 14, color: S.text2, lineHeight: 1.5, maxWidth: 640 } as const

export function MenuPanel({ model, section, onSection, onClose, emptyNavMessage }: {
  model: ShellNavModel
  section: PanelSection
  onSection: (s: PanelSection) => void
  /** `restore`: return focus to the opener (Esc / Back); a screen opened from
   *  the panel does not. */
  onClose: (restore: boolean) => void
  emptyNavMessage: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    navRef.current?.querySelector<HTMLElement>('[aria-current="true"]')?.focus()
    // Focus once, when the panel opens; a section change keeps focus where it is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sections: { id: PanelSection; label: string; icon: ReactNode; count?: number }[] = [
    { id: 'home', label: 'Home', icon: <House size={18} aria-hidden /> },
    { id: 'open', label: 'Open', icon: <FolderOpen size={18} aria-hidden /> },
    { id: 'pinned', label: 'Pinned', icon: <Pin size={18} aria-hidden />, count: model.pinned.length },
    { id: 'recent', label: 'Recent', icon: <Clock size={18} aria-hidden />, count: model.recent.length },
    ...(model.editorsEnabled
      ? [{ id: 'editors' as PanelSection, label: 'Open editors', icon: <Pencil size={18} aria-hidden />, count: model.editors.length }]
      : []),
  ]
  const picked = () => onClose(false)

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // A menu inside the panel (a context menu) handles its own Esc first.
    if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onClose(true); return }
    if (e.key !== 'Tab') return
    const els = tabbables(ref.current)
    if (!els.length) return
    const first = els[0]
    const last = els[els.length - 1]
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  let body: ReactNode
  switch (section) {
    case 'home':
      body = (
        <>
          <h1 style={h1Style}>Home</h1>
          <h2 style={h2Style}>Pinned</h2>
          <PinnedList model={model} onPicked={picked} reorder={false} />
          <h2 style={h2Style}>Recent</h2>
          <RecentList model={model} onPicked={picked} />
        </>
      )
      break
    case 'open':
      body = (
        <>
          <h1 style={h1Style}>Open</h1>
          <p style={leadStyle}>Every screen in this application. Right-click a screen to pin it to quick access.</p>
          {model.roots.length === 0
            ? <p style={leadStyle}>{emptyNavMessage}</p>
            : <MenuTree model={model} onPicked={picked} label="All screens" />}
        </>
      )
      break
    case 'pinned':
      body = (
        <>
          <h1 style={h1Style}>Pinned</h1>
          <p style={leadStyle}>
            Pinned screens are your quick access toolbar at the top of the window. Reorder or unpin them here.
          </p>
          <PinnedList model={model} onPicked={picked} />
        </>
      )
      break
    case 'recent':
      body = (
        <>
          <h1 style={h1Style}>Recent</h1>
          <RecentList model={model} onPicked={picked} />
        </>
      )
      break
    case 'editors':
    default:
      body = (
        <>
          <h1 style={h1Style}>Open editors</h1>
          <p style={leadStyle}>Records you minimised while adding or editing. Everything typed so far is kept.</p>
          <EditorsList model={model} onPicked={picked} />
        </>
      )
  }

  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={onKey}
      {...tw('action', 'shell-office-panel', { section })}
      style={{
        position: 'absolute', inset: 0, zIndex: 40, display: 'flex', background: S.panel, color: S.text,
      }}>
      <nav ref={navRef} aria-label="Menu sections" className="tw-oc-bs"
        onKeyDown={(e) => {
          const els = [...(navRef.current?.querySelectorAll<HTMLElement>('[data-section]') || [])]
          if (moveFocus(els, e.key, false)) e.preventDefault()
        }}
        style={{
          width: 230, flexShrink: 0, display: 'flex', flexDirection: 'column', padding: '10px 0',
          background: ACCENT_SOLID, color: '#fff', overflowY: 'auto',
        }}>
        <button type="button" onClick={() => onClose(true)} aria-label="Close menu" title="Close menu"
          className="tw-oc-bs-item"
          {...tw('action', 'shell-office-panel-close')}
          style={{
            width: 44, height: 44, margin: '4px 0 14px 14px', flexShrink: 0, padding: 0,
            border: '1.5px solid #fff', borderRadius: '50%', background: 'transparent', color: '#fff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
          <ArrowLeft size={18} aria-hidden />
        </button>
        {sections.map((s) => {
          const on = s.id === section
          return (
            <button key={s.id} type="button" data-section={s.id} className="tw-oc-bs-item"
              aria-current={on ? 'true' : undefined}
              onClick={() => onSection(s.id)}
              {...tw('action', `shell-office-panel:${s.id}`, { selected: on })}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, minHeight: 46, padding: '0 22px',
                border: 'none', background: on ? 'rgba(0,0,0,0.24)' : 'transparent', color: '#fff',
                fontFamily: 'inherit', fontSize: 15, fontWeight: on ? 600 : 400, textAlign: 'left',
                cursor: 'pointer', flexShrink: 0,
              }}>
              {s.icon}
              <span style={{ flex: 1, minWidth: 0 }}>{s.label}</span>
              {typeof s.count === 'number' && s.count > 0 && (
                <span aria-label={`${s.count}`} style={{
                  minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11,
                  background: 'rgba(255,255,255,0.25)', fontSize: 13, fontWeight: 700,
                  lineHeight: '22px', textAlign: 'center',
                }}>{s.count}</span>
              )}
            </button>
          )
        })}
      </nav>
      <div className="tw-oc-bs-pane" style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: '28px 40px 40px' }}>
        <div style={{ maxWidth: 760 }}>{body}</div>
      </div>
    </div>
  )
}
